"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, PhoneCall, ArrowRight, Search, MapPin, CircleCheck as CheckCircle2, Mail, Sparkles } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqData = [{
  id: "item-1",
  question: "Why should my Belfast business focus on Local SEO instead of general UK SEO?",
  answer: "Belfast customers search with distinct local intent, from 'SEO consultant near Cathedral Quarter' to 'B2B services in Titanic Quarter'. Local SEO ensures your business dominates the Google Map Pack and organic local listings directly where Northern Ireland buyers are searching. Rather than competing against nationwide giants for high-volume, low-converting UK terms, I help you capture highly targeted local traffic with immediate buying intent."
}, {
  id: "item-2",
  question: "Will I work directly with you, Yassine, or get passed to an agency account manager?",
  answer: "You work directly with me—100% of the time. I am an independent freelance SEO consultant, not an agency. There are no account managers, account executives, or outsourced junior teams handling your account. Every audit, keyword strategy, technical fix, and monthly reporting call is conducted personally by me."
}, {
  id: "item-3",
  question: "How quickly can a Belfast company expect to see tangible rankings and leads?",
  answer: "Local SEO typically delivers initial visibility improvements within 6 to 12 weeks. Google Business Profile optimisations and local citation updates often yield quicker Map Pack visibility, while complex organic site strategy built for high-competition Belfast sectors usually reaches full momentum between months 3 and 6. I provide transparent timeline estimates during our initial strategy session."
}, {
  id: "item-4",
  question: "What is Google Business Profile optimisation and why is it critical in Belfast?",
  answer: "Your Google Business Profile (formerly Google My Business) is your primary storefront in Google Maps and local search results. I fully audit and optimize your GBP profile—correcting categories, crafting keyword-rich descriptions, setting up local product showcase posts, managing photo geo-tagging, and advising on review strategy—so you consistently rank in the top 3 Map Pack results across Belfast and surrounding areas."
}, {
  id: "item-5",
  question: "Do you cover Belfast suburbs and surrounding Northern Ireland towns?",
  answer: "Yes, absolutely. My Belfast Local SEO strategies cover central Belfast (BT1–BT15) as well as Lisburn, Newtownabbey, Holywood, Bangor, Castlereagh, Dundonald, and regional hubs across County Antrim and County Down. If your target market spans multiple NI locations, I build location-specific landing pages tailored for each territory."
}, {
  id: "item-6",
  question: "How do you optimize my website for Belfast-specific search intent and local keywords?",
  answer: "I conduct localized keyword research targeting high-converting Belfast search queries, integrate geo-targeted schema markup (LocalBusiness JSON-LD), optimize NAP (Name, Address, Phone) consistency across local directories, and optimize on-page signals without spammy keyword stuffing. The goal is to make your authority indisputable to search engines."
}, {
  id: "item-7",
  question: "How do local citations and Northern Ireland backlinks help my search rankings?",
  answer: "Search engines rely on local mentions and authoritative backlinks from recognized Northern Ireland business directories, regional news, and industry bodies to verify your local prominence. I identify and secure high-grade local citations and contextual link opportunities that build authentic domain trust for your Belfast business."
}, {
  id: "item-8",
  question: "Can you help my business recover from a drop in local search rankings or Google penalty?",
  answer: "Yes. I offer comprehensive SEO Audits that diagnose algorithmic ranking drops, technical indexing blocks, duplicate local listings, or unhelpful content issues. Once identified, I construct a step-by-step technical recovery plan to restore and exceed your previous Belfast organic traffic levels."
}, {
  id: "item-9",
  question: "How do AI Search Optimisation and Google AI Overviews impact Belfast Local SEO?",
  answer: "Google AI Overviews, ChatGPT, and Perplexity are changing how consumers research local services. My AI Search Optimisation approach ensures your Belfast business is structured, entity-validated, and cited as a trusted source in AI-generated search answers, keeping you ahead as search habits evolve."
}, {
  id: "item-10",
  question: "What deliverables and reporting will I receive each month?",
  answer: "You receive a clear, jargon-free monthly performance report directly from me. It covers keyword position tracking in Belfast, organic traffic gains, Google Map Pack impressions, conversion tracking, and completed strategy deliverables. You will always know exactly what work was done and how it impacts your pipeline."
}, {
  id: "item-11",
  question: "What contract commitments do you require for Local SEO consultancy?",
  answer: "I offer flexible consultancy models based on your growth targets and stage. Because SEO requires sustained strategic momentum to generate compounding returns, I recommend initial 3-to-6 month retainers, but I focus on retaining clients through demonstrated results rather than restrictive long-term lock-ins."
}, {
  id: "item-12",
  question: "How do I get started with a Belfast Local SEO strategy session?",
  answer: "Getting started is straightforward. You can book a free SEO consultation directly through my site. I will analyze your current search footprint in Belfast prior to our call and walk you through actionable growth opportunities tailored specifically for your business."
}];
export default function BelfastFaq() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = faqData.filter(item => item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase()));
  return <section id="belfastlocalseofaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border/60 transition-colors duration-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="flex items-center justify-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase bg-secondary text-secondary-foreground border border-border">
              <MapPin className="size-3.5 text-primary mr-1" />
              Belfast Local Search Insights
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-default text-foreground tracking-tight leading-tight">
            Belfast Local SEO <span className="text-primary">Questions Answered</span>
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers from Yassine Essaadi on how tailored Local SEO strategies drive qualified leads and dominant search presence for Belfast businesses.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="mt-8 max-w-md mx-auto relative">
            <div className="relative flex items-center">
              <Search className="absolute left-3.5 size-4 text-muted-foreground pointer-events-none" />
              <input type="text" placeholder="Search Belfast SEO questions..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-card border border-input rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all shadow-xs" />
            </div>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-8">
            {filteredFaqs.length > 0 ? <Accordion type="single" collapsible className="w-full space-y-4">
                {filteredFaqs.map((faq, i) => <div data-index={i} key={faq.id}>
                    <AccordionItem value={faq.id} className="bg-card border border-border rounded-xl px-5 py-1 shadow-xs hover:border-primary/40 transition-colors">
                      <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-primary hover:no-underline hover:text-primary/90 py-4 gap-3">
                        <span className="flex items-start gap-3">
                          <HelpCircle className="size-5 text-primary shrink-0 mt-0.5" />
                          <span>{faq.question}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-8 border-t border-border/40 mt-1">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion> : <div className="text-center py-12 bg-card border border-border rounded-xl p-6">
                <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-foreground font-medium text-lg">No matching questions found</p>
                <p className="text-muted-foreground text-sm mt-1">
                  Try searching with different keywords or contact Yassine directly below.
                </p>
                <Button variant="outline" onClick={() => setSearchQuery("")} className="mt-4 rounded-lg">
                  Clear search
                </Button>
              </div>}
          </div>

          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/60 rounded-full blur-2xl pointer-events-none -z-0" />

              <div className="relative z-10 space-y-6">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                  <Sparkles className="size-3.5" />
                  <span>Direct Expert Access</span>
                </div>

                <div className="space-y-2">
                  <h3 className="text-xl sm:text-2xl font-bold font-default text-foreground">
                    Have a specific question about your site?
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {"I'm Yassine Essaadi. Send me your website link and current local search challenges, and I will personally review your search presence."}
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <div className="flex items-center gap-3 text-sm text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>No agency gatekeepers or salespeople</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Tailored Belfast market recommendations</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground font-medium">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Honest assessment with zero sales pressure</span>
                  </div>
                </div>

                <div className="pt-4 border-t border-border space-y-3">
                  <Button asChild variant="default" size="lg" className="w-full rounded-xl bg-primary text-primary-foreground font-medium shadow-sm hover:bg-primary/90 justify-center group">
                    <Link to="/ContactPage">
                      <span>Book Free Strategy Session</span>
                      <ArrowRight className="size-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="lg" className="w-full rounded-xl border-border hover:bg-accent hover:text-accent-foreground justify-center">
                    <a href="mailto:contact@ye-consultancy.com">
                      <Mail className="size-4 mr-2 text-primary" />
                      <span>contact@ye-consultancy.com</span>
                    </a>
                  </Button>

                  <Button asChild variant="ghost" size="sm" className="w-full text-muted-foreground hover:text-foreground justify-center text-xs">
                    <a href="tel:+447490984430">
                      <PhoneCall className="size-3.5 mr-1.5 text-primary" />
                      <span>+44 7490 984430</span>
                    </a>
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}